/**
 * ./src/modules/product/components/collection/CollectionListGrid.tsx
 *
 * CollectionListGrid component to display the content of the GetInspired page
 *
 * @copyright DesignShop, 2024
 * @since 08/19/2024
 */
import React, { createRef, useMemo, useState } from 'react';

import { HitType } from '@/modules/common/lib/abtastyStub';

import { useAddToCart } from '@/modules/cart/hooks/useAddToCart';
import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Button } from '@/modules/common/components/Button';
import { Heart } from '@/modules/common/components/icons';
import useFeatureFlag from '@/modules/common/hooks/useFeatureFlag';
import useIsMobile from '@/modules/common/hooks/useIsMobile';
import { useQueryParams } from '@/modules/common/hooks/useQueryParams';
import { cn } from '@/modules/common/lib/utils';
import { SamplePrice } from '@/modules/product/components/sample/SamplePrice';
import { useVisibleItemsTracking } from '@/modules/product/hooks/useVisibleItemsTracking';
import { ContentfulCollection } from '@/modules/product/models/collection.interface';
import { ProductPdpInterface } from '@/modules/product/models/product-pdp.interface';
import {
  DataLayerFeature,
  addToWishlistDataLayerEvent,
} from '@/modules/product/services/data-layer-events/add-to-wishlist';
import { CollectionDataLayer } from '@/modules/product/services/data-layer-events/utils';
import { isSampleNotOffered } from '@/modules/product/services/sampleHelpers';
import { SampleCard, SampleCardSkeleton } from '@/modules/sample-card';
import {
  ShoppingListSaveType,
  useShoppingListActions,
} from '@/modules/shopping-list/hooks/useShoppingListActions';

import useGridLayout from '../../hooks/useGridLayout';
import { useSampleCardVariants } from '../../hooks/useSampleCardVariants';

const gridClassNames = (gridLayout: 'thumbnail' | 'side-by-side' | null) =>
  cn(
    'grid h-fit gap-2 md:grid-cols-[repeat(auto-fill,minmax(185px,1fr))] lg:grid-cols-[repeat(auto-fill,minmax(262px,1fr))]',
    {
      'grid-cols-1': gridLayout === 'thumbnail',
      'grid-cols-2': gridLayout === 'side-by-side',
    }
  );

export const CollectionListGridSkeleton = () => {
  const { gridLayout } = useGridLayout();

  return (
    <div className={gridClassNames(gridLayout)} data-testid="collection-list-grid-skeleton">
      {Array.from({ length: 12 }).map((_, index) => (
        <SampleCardSkeleton key={index} />
      ))}
    </div>
  );
};

type SelectedProduct = {
  sku: string;
  color?: string;
  image?: {
    url?: string | null;
    label?: string | null;
  }[];
  price: number;
  productId: string;
  productName: string;
};

type CollectionListGrid = {
  products: ProductPdpInterface[];
  collection: ContentfulCollection;
  exactMatches?: string[];
};
export const CollectionListGrid = ({
  products,
  exactMatches = [],
  collection,
}: CollectionListGrid) => {
  const { sendFeatureFlagTracking } = useFeatureFlag();

  const [activeCategory] = useQueryParams<string>('c', 'All');
  const [sortPrice] = useQueryParams<string>('sort', '');
  const { gridLayout } = useGridLayout();
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const isMobile = useIsMobile('sm');

  const applied_filters = useMemo(() => {
    return [{ type: 'Category', value: activeCategory || 'All' }];
  }, [activeCategory]);

  const viewMode = gridLayout === 'side-by-side' && isMobile ? 'compact' : 'default';
  const { enhancedProducts } = useSampleCardVariants(products);

  const images = selectedProducts.slice(0, 3).map((product) => product.image?.[0]?.url || '');
  const colors = selectedProducts.slice(0, 3).map((product) => product.color || '');

  const { bulkAddProductsToShoppingList } = useShoppingListActions({
    bulkProducts: selectedProducts,
    type: ShoppingListSaveType.Product,
  });

  const totalPrice = selectedProducts.reduce((acc, product) => acc + product.price, 0);

  // Create a filtered products array that reflects what's actually displayed
  const filteredProducts = useMemo(() => {
    return enhancedProducts
      .filter((product) => {
        if (activeCategory === 'All') {
          return true;
        }

        const categoriesAvailable = product?.category?.name.split(' > ') || [];
        return categoriesAvailable.some((category) => category === activeCategory);
      })
      .sort((a, b) => {
        if (!sortPrice) {
          return 0;
        }
        const aPrice = a.samplePrice;
        const bPrice = b.samplePrice;
        if (sortPrice === 'asc') {
          return aPrice - bPrice;
        }
        return bPrice - aPrice;
      });
  }, [enhancedProducts, activeCategory, sortPrice]);

  const productRefs = useMemo(
    () => filteredProducts.map(() => createRef<HTMLDivElement>()),
    [filteredProducts]
  );

  useVisibleItemsTracking({
    products: filteredProducts,
    productRefs,
    collection,
    shoppingListProducts: [],
    feature: DataLayerFeature.Collections,
    where: DataLayerWhere.Collections,
    itemListId: collection.slug ?? '',
    itemListName: collection.collectionName ?? '',
    itemListType: 'Editorial collection',
    applied_filters,
  });

  const handleCheckProduct = (checked: boolean, sku: string) => {
    if (checked) {
      setSelectedProducts((prev) => {
        const product = products.find((product) => product.sku === sku);
        if (product) {
          return [
            ...prev,
            {
              sku,
              color: product.color || '',
              image:
                product.images?.map((image) => ({
                  url: image.url,
                  label: image.label,
                })) || [],
              price: product.samplePrice || 0,
              productId: product.id,
              productName: product.name || '',
            },
          ];
        }
        return prev;
      });
    } else {
      setSelectedProducts((prev) => prev.filter((product) => product.sku !== sku));
    }
  };

  const handleAddToWishlist = async () => {
    await bulkAddProductsToShoppingList(false);
    addToWishlistDataLayerEvent({
      feature: DataLayerFeature.Collections,
      where: DataLayerWhere.ProductCard,
      method: 'Multi-select',
      type: ShoppingListSaveType.Collection,
      shoppingListProducts: null,
      product: {
        slug: collection.slug ?? '',
        title: collection.collectionName ?? '',
      } satisfies CollectionDataLayer,
      item_list_type: 'Editorial collection',
      item_list_name: collection.collectionName ?? '',
      item_list_id: collection.slug ?? '',
      applied_filters,
    });
    setSelectedProducts([]);

    sendFeatureFlagTracking({
      type: HitType.EVENT,
      category: 'Action Tracking',
      action: 'Product Saved',
      label: collection.collectionName,
    });
  };

  const { onAddToCartBulk } = useAddToCart({
    where: DataLayerWhere.Collections,
    appliedFilters: applied_filters,
  });

  const handleAddProductsToCart = async () => {
    const productsToAdd = products.filter(
      (product) =>
        selectedProducts.some((selectedProduct) => selectedProduct.sku === product.sku) &&
        !isSampleNotOffered(product.sampleAction)
    );

    onAddToCartBulk(
      productsToAdd.map((product) => ({
        ...product,
        exactMatch: exactMatches?.includes(product?.sku || ''),
      }))
    );
    setSelectedProducts([]);
  };

  if (products.length === 0) {
    return <div className="px-8">No products</div>;
  }

  return (
    <div className={gridClassNames(gridLayout)}>
      {filteredProducts.map((product, index) => {
        return (
          <div
            key={product.sku}
            ref={productRefs[index]}
            data-product-id={product.id}
            data-index={index}
            className="flex flex-col"
          >
            <SampleCard
              index={index}
              key={product.sku}
              where={DataLayerWhere.ProductCard}
              isSelectable={product.isOnStock && !isSampleNotOffered(product.sampleAction)}
              isSelected={selectedProducts.some(
                (selectedProduct) => selectedProduct.sku === product?.sku
              )}
              onCheck={(checked: boolean) => handleCheckProduct(checked, product?.sku || '')}
              {...product}
              feature={DataLayerFeature.Collections}
              item_list_type="Editorial collection"
              {...(collection.collectionName && {
                item_list_name: collection.collectionName,
              })}
              {...(collection.slug && {
                item_list_id: collection.slug,
                hrefQueryParams: {
                  collection: collection.slug,
                },
              })}
              appliedFilters={applied_filters}
              data-testid="sample-card"
              viewMode={viewMode}
            />
          </div>
        );
      })}

      <div
        className={`animate-fade-in-up visible fixed bottom-5 left-0 right-0 z-10 mx-auto flex w-fit items-center gap-3 rounded-xxl border border-gray-200 bg-white p-4 opacity-100 transition-all duration-100 ease-in-out ${selectedProducts.length === 0 ? 'invisible translate-y-full opacity-0' : ''} `}
      >
        <div className="flex h-14 w-14 flex-shrink-0 flex-wrap items-center justify-center rounded-lg bg-gray-50">
          {images.length > 0 ?
            images.map((image, i) => (
              <div
                key={i}
                className="h-1/2 w-1/2 overflow-hidden"
                style={{
                  backgroundImage: `url(${image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundColor: colors[i] || 'white',
                }}
              />
            ))
          : <Heart />}
        </div>
        <div className="flex-shrink-0">
          <p className="font-bold">Selected samples</p>
          <p className="text-gray-500">
            <strong>{selectedProducts.length}</strong> products{' '}
            <SamplePrice priceInCents={totalPrice} /> total
          </p>
        </div>
        <Button
          onClick={handleAddToWishlist}
          data-testid="add-to-wishlist-button"
          size="s"
          variant="secondary"
        >
          Save
        </Button>
        <Button
          onClick={handleAddProductsToCart}
          data-testid="add-to-cart-button"
          size="s"
          variant="primary"
        >
          Add Samples
        </Button>
      </div>
    </div>
  );
};
